/**
 * Error Handling Utilities
 * Centralized error handling and normalization
 */

import { AxiosError } from 'axios'
import { ApiError } from '../types'
import { ERROR_MESSAGES } from '../constants/app.constants'

/**
 * Normalize API errors to a consistent format
 */
export const normalizeError = (error: unknown): ApiError => {
  // Handle Axios errors
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<any>
    
    const message = axiosError.response?.data?.message 
      || axiosError.response?.data?.error
      || axiosError.message
      || ERROR_MESSAGES.GENERIC
    
    const status = axiosError.response?.status || 500
    
    // A Nest ValidationPipe whose exceptionFactory passes the raw errors (QLM-Backend main.ts)
    // sends ValidationError objects, not strings: show their constraint texts.
    const toText = (m: any): string =>
      typeof m === 'string'
        ? m
        : Object.values(m?.constraints ?? {}).join(', ') || m?.property || String(m)

    return {
      message: (Array.isArray(message) ? message : [message]).map(toText),
      status,
      error: axiosError.response?.data?.error,
    }
  }
  
  // Handle Error objects
  if (error instanceof Error) {
    return {
      message: error.message || ERROR_MESSAGES.GENERIC,
      status: 500,
    }
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
      status: 500,
    }
  }
  
  // Unknown error type
  return {
    message: ERROR_MESSAGES.GENERIC,
    status: 500,
  }
}

/**
 * Format error message for display
 */
export const formatErrorMessage = (error: ApiError | unknown): string => {
  const normalized = error && typeof error === 'object' && 'message' in error 
    ? error as ApiError 
    : normalizeError(error)
  
  if (Array.isArray(normalized.message)) {
    return normalized.message.join(', ')
  }
  
  return normalized.message
}

/**
 * Check if error is a specific HTTP status
 */
export const isErrorStatus = (error: unknown, status: number): boolean => {
  const normalized = normalizeError(error)
  return normalized.status === status
}

/**
 * Check if error is authorization error
 */
export const isAuthError = (error: unknown): boolean => {
  return isErrorStatus(error, 401) || isErrorStatus(error, 403)
}

/**
 * Check if error is not found
 */
export const isNotFoundError = (error: unknown): boolean => {
  return isErrorStatus(error, 404)
}

/**
 * Check if error is validation error
 */
export const isValidationError = (error: unknown): boolean => {
  return isErrorStatus(error, 400) || isErrorStatus(error, 422)
}

/**
 * Check if error is server error
 */
export const isServerError = (error: unknown): boolean => {
  const normalized = normalizeError(error)
  return normalized.status >= 500
}

/**
 * Log error for debugging/monitoring
 */
export const logError = (error: unknown, context?: string): void => {
  const normalized = normalizeError(error)
  
  if (process.env.NODE_ENV === 'development') {
    console.group(`❌ Error${context ? ` in ${context}` : ''}`)
    console.error('Message:', normalized.message)
    console.error('Status:', normalized.status)
    console.error('Original:', error)
    console.groupEnd()
  }
  
  // In production, send to error tracking service (e.g., Sentry)
  // if (process.env.NODE_ENV === 'production') {
  //   sendToErrorTracking(normalized, context)
  // }
}

/**
 * Handle error with logging and return formatted message
 */
export const handleError = (error: unknown, context?: string): string => {
  logError(error, context)
  return formatErrorMessage(error)
}

